import type { jsPDF } from "jspdf";

/** 지원하는 언어 타입 */
type LanguageType = "korean" | "latin";

/** 폰트 설정 */
interface FontConfig {
  name: string;
  localPaths: string[]; // 순서대로 시도
}

/** 언어별 폰트 설정 */
const FONT_CONFIGS: Record<LanguageType, FontConfig> = {
  korean: {
    name: "Pretendard",
    localPaths: ["/fonts/Pretendard-Regular.ttf"],
  },
  latin: {
    name: "Pretendard",
    localPaths: ["/fonts/Pretendard-Regular.ttf"],
  },
};

/** 유니코드 범위로 언어 감지 */
const detectLanguages = (text: string): Set<LanguageType> => {
  const languages = new Set<LanguageType>();

  for (const char of text) {
    const code = char.charCodeAt(0);

    // 한글 (가-힣, ㄱ-ㅎ, ㅏ-ㅣ)
    if (
      (code >= 0xac00 && code <= 0xd7af) ||
      (code >= 0x3130 && code <= 0x318f)
    ) {
      languages.add("korean");
    }
    // 기본 라틴
    else if (code >= 0x0000 && code <= 0x007f) {
      languages.add("latin");
    }
  }

  languages.add("korean");
  languages.add("latin");

  return languages;
};

/** 폰트 로딩 및 PDF 등록 관리 싱글톤 */
class FontManager {
  private fontCache: Map<string, string> = new Map();
  private boldFontCache: Map<string, string> = new Map();
  private loadingPromises: Map<string, Promise<string | null>> = new Map();

  /** 폰트 파일을 Base64로 변환 */
  private async toBase64(url: string): Promise<string | null> {
    try {
      const res = await fetch(url);
      if (!res.ok) return null;
      const buf = await res.arrayBuffer();
      const bytes = new Uint8Array(buf);
      const binary = Array.from(bytes, (b) => String.fromCharCode(b)).join("");
      return btoa(binary);
    } catch {
      return null;
    }
  }

  /** 단일 폰트 로드 (로컬 우선, CDN 폴백) */
  private async loadFont(config: FontConfig): Promise<string | null> {
    const cacheKey = config.name;
    
    // 이미 캐시에 있으면 반환
    if (this.fontCache.has(cacheKey)) {
      return this.fontCache.get(cacheKey)!;
    }
    
    // 이미 로딩 중이면 해당 Promise 반환
    if (this.loadingPromises.has(cacheKey)) {
      return this.loadingPromises.get(cacheKey)!;
    }
    
    const loadPromise = (async () => {
      let fontBase64: string | null = null;

      for (const path of config.localPaths) {
        fontBase64 = await this.toBase64(path);
        if (fontBase64) break;
      }

      if (fontBase64) {
        this.fontCache.set(cacheKey, fontBase64);
      } else {
        console.warn(`폰트 로드 실패: ${config.name}`);
      }
      
      this.loadingPromises.delete(cacheKey);
      return fontBase64;
    })();
    
    this.loadingPromises.set(cacheKey, loadPromise);
    return loadPromise;
  }

  /** Pretendard Bold 폰트 로드 */
  private async loadBoldFont(): Promise<string | null> {
    const cacheKey = "Pretendard-Bold";
    
    if (this.boldFontCache.has(cacheKey)) {
      return this.boldFontCache.get(cacheKey)!;
    }
    
    // Bold 또는 SemiBold 시도
    let boldBase64 = await this.toBase64("/fonts/Pretendard-Bold.ttf");
    if (!boldBase64) {
      boldBase64 = await this.toBase64("/fonts/Pretendard-SemiBold.ttf");
    }
    
    if (boldBase64) {
      this.boldFontCache.set(cacheKey, boldBase64);
    }
    
    return boldBase64;
  }

  /** 텍스트 분석하여 필요한 언어 목록 반환 */
  analyzeText(text: string): LanguageType[] {
    const languages = detectLanguages(text);
    return Array.from(languages);
  }

  /** 필요한 폰트들을 미리 로드 */
  async preloadFonts(languages: LanguageType[]): Promise<void> {
    const loadPromises = languages.map((lang) => {
      const config = FONT_CONFIGS[lang];
      return this.loadFont(config);
    });
    await Promise.all(loadPromises);
  }

  /** jsPDF에 필요한 폰트 등록 */
  async setupPdf(pdf: jsPDF, text?: string): Promise<boolean> {
    const languages = text ? this.analyzeText(text) : (["korean", "latin"] as LanguageType[]);

    try {
      await this.preloadFonts(languages);

      const pretendardBase64 = this.fontCache.get("Pretendard");
      if (pretendardBase64) {
        if (!(pdf as any).__pretendardLoaded) {
          pdf.addFileToVFS("Pretendard-Regular.ttf", pretendardBase64);
          pdf.addFont("Pretendard-Regular.ttf", "Pretendard", "normal");

          const boldBase64 = await this.loadBoldFont();
          if (boldBase64) {
            pdf.addFileToVFS("Pretendard-Bold.ttf", boldBase64);
            pdf.addFont("Pretendard-Bold.ttf", "Pretendard", "bold");
          } else {
            pdf.addFont("Pretendard-Regular.ttf", "Pretendard", "bold");
          }

          (pdf as any).__pretendardLoaded = true;
        }
      }

      return !!pretendardBase64;
    } catch (error) {
      console.warn("PDF 폰트 설정 실패:", error);
      return false;
    }
  }

  /** 텍스트에 적합한 폰트 이름 반환 */
  getFontForText(_text: string): string {
    return "Pretendard";
  }

  /** 폰트 스타일 설정 */
  setFont(pdf: jsPDF, bold = false, loaded = false, text?: string): void {
    if (!loaded) {
      pdf.setFont("helvetica", bold ? "bold" : "normal");
      return;
    }
    
    // 텍스트가 제공되면 적합한 폰트 선택
    const fontName = text ? this.getFontForText(text) : "Pretendard";
    pdf.setFont(fontName, bold ? "bold" : "normal");
  }

  /** 폰트 캐시 상태 확인 */
  isFontLoaded(language: LanguageType): boolean {
    const config = FONT_CONFIGS[language];
    return this.fontCache.has(config.name);
  }
}

export const fontManager = new FontManager();
